/**
 * User Dashboard — "Variant B" (Path focus) composition.
 *
 * Reads `UserDashboardData` via react-query and runs it through
 * `buildDashboardVM` (`components/dashboard/variantB/adapter.ts`) before
 * rendering the six Variant B sections in this order:
 *
 *   1. Greeting (`<Greeting>`)
 *   2. Recommended hero (`<RecommendedHero>`) — weakest topic as drill
 *   3. Certification path + Skill IQ side rail (`<LearningPath>` + `<SkillIQCard>` + `<NextMilestoneCard>`)
 *   4. Recommended drills carousel (`<DrillsCarousel>`)
 *   5. Domain catalog with per-domain Skill IQ (`<DomainCatalog>`)
 *   6. Score trajectory (`<ScoreTrajectory>`)
 *
 * Empty / loading / error / unauthenticated branches are handled before
 * the section composition. The "Reset progress" button is intentionally
 * demoted to a discreet footer-aligned action (not in the Variant B
 * design but still a mandatory app feature).
 */
import { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { UserDashboardData } from '../types/admin';
import { fetchUserDashboard } from '../api/admin';
import { resetStats } from '../api/auth';
import { useAuthStore } from '../stores/authStore';
import { useCertStore } from '../stores/certStore';
import { useCertification } from '../hooks/useCertification';
import { useIsPremium } from '../stores/premiumStore';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { AppShell } from '../components/ui';
import OnboardingWizard from '../components/OnboardingWizard';
import DailyLimitBanner from '../components/DailyLimitBanner';
import { InlineAd } from '../components/AdUnit';
import {
  buildDashboardVM,
  Greeting,
  RecommendedHero,
  SectionHead,
  LearningPath,
  SkillIQCard,
  NextMilestoneCard,
  DrillsCarousel,
  DomainCatalog,
  ScoreTrajectory,
} from '../components/dashboard/variantB';

/** Danger confirmation modal for resetting stats — kept inline since it's
 *  not part of the Variant B design but remains a required app feature. */
function ResetModal({ onConfirm, onCancel, loading }: { onConfirm: () => void; onCancel: () => void; loading: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md px-4">
      <div className="w-full max-w-md rounded-3xl bg-white dark:bg-card-dark border border-slate-200 dark:border-white/10 shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-red-100 dark:bg-red-500/10 text-red-600 dark:text-red-400">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </span>
          <div>
            <h3 className="font-bold text-slate-900 dark:text-white">{t('dashboard.page.resetTitle')}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('dashboard.page.resetWarning')}</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">{t('dashboard.page.resetDescription')}</p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            {t('dashboard.page.resetCancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 rounded-xl text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center gap-2"
          >
            {loading && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {t('dashboard.page.resetConfirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

function DashboardEmptyState() {
  const { t } = useTranslation();
  const cert = useCertStore((s) => ({ name: s.certName }));
  const certName = cert?.name ?? t('dashboard.emptyState.certNameFallback');
  return (
    <div className="rounded-3xl border border-slate-200/70 bg-white p-10 text-center shadow-sm dark:border-white/10 dark:bg-card-dark">
      <span className="text-6xl" aria-hidden="true">🚀</span>
      <h2 className="mt-4 text-2xl font-extrabold text-slate-900 dark:text-white">
        {(() => {
          const raw = t('dashboard.emptyState.title', { certName: '___CERT___' });
          const [before, rest] = raw.split('<gradient>');
          const [highlight, after] = (rest ?? '').split('</gradient>');
          return (
            <>
              {before?.replace('___CERT___', '')}
              <span className="text-blue-600 dark:text-blue-400">
                {(highlight ?? '').replace('___CERT___', certName)}
              </span>
              {after?.replace('___CERT___', '')}
            </>
          );
        })()}
      </h2>
      <p className="mt-2 mx-auto max-w-md text-slate-600 dark:text-slate-400">
        {t('dashboard.emptyState.description')}
      </p>
      <div className="mt-6 mx-auto grid max-w-md gap-4 sm:grid-cols-2">
        <Link
          to="/study"
          className="rounded-xl bg-blue-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)] transition-all hover:bg-blue-700"
        >
          {t('dashboard.emptyState.startStudying')}
        </Link>
        <Link
          to="/exam"
          className="rounded-xl border border-slate-200 px-4 py-3 text-center text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/10"
        >
          {t('dashboard.emptyState.takePracticeExam')}
        </Link>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  useDocumentTitle(t('dashboard.page.documentTitle'));
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const certCode = useCertStore((s) => s.certCode);
  const certName = useCertStore((s) => s.certName);
  const { data: certData } = useCertification();
  const isPremium = useIsPremium();

  const [showReset, setShowReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => user?.onboarding_completed === false);

  const handleOnboardingComplete = useCallback(() => setShowOnboarding(false), []);

  const { data: dashboard, isLoading, error } = useQuery<UserDashboardData>({
    queryKey: ['user-dashboard', certCode],
    queryFn: () => fetchUserDashboard(certCode),
    staleTime: 60_000,
  });

  const handleReset = async () => {
    setResetting(true);
    try {
      await resetStats();
      await queryClient.invalidateQueries({ queryKey: ['user-dashboard', certCode] });
      setShowReset(false);
      setResetDone(true);
      setTimeout(() => setResetDone(false), 4000);
    } finally {
      setResetting(false);
    }
  };

  const passMark = certData?.passing_score_percent ?? 70;
  const avgSec = dashboard?.time_analytics?.avg_seconds_per_question ?? null;
  const vm = useMemo(
    () => (dashboard ? buildDashboardVM(dashboard, passMark, avgSec) : null),
    [dashboard, passMark, avgSec],
  );

  const recommendedDomain = useMemo(() => {
    if (!vm?.recommended) return null;
    return vm.domains.find((d) => d.name === vm.recommended!.domName) ?? null;
  }, [vm]);

  const examWeight = recommendedDomain?.weight ?? null;
  const userName = user?.display_name || user?.username || null;

  return (
    <AppShell maxWidth="7xl">
      {showOnboarding && <OnboardingWizard onComplete={handleOnboardingComplete} />}
      {showReset && <ResetModal onConfirm={handleReset} onCancel={() => setShowReset(false)} loading={resetting} />}

      {/* Greeting */}
      <Greeting userName={userName} certName={certName} />

        {/* Reset success toast */}
        {resetDone && (
          <div className="mb-5 inline-flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-500/20 dark:bg-green-500/10 dark:text-green-300">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
            {t('dashboard.page.resetSuccess')}
          </div>
        )}

        {/* Daily limit (free users) */}
        <DailyLimitBanner />

        {/* Upgrade nudge (free users) — slim, between greeting and content */}
        {!isPremium && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-200/60 bg-gradient-to-r from-blue-50 to-violet-50 p-4 dark:border-blue-500/20 dark:from-blue-500/10 dark:to-violet-500/10">
            <div>
              <h3 className="text-[14px] font-extrabold text-slate-900 dark:text-white">
                {t('dashboard.page.upgradeTitle')}
              </h3>
              <p className="mt-0.5 text-[12.5px] text-slate-600 dark:text-slate-300">
                {t('dashboard.page.upgradeDescription')}
              </p>
            </div>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 rounded-[11px] bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)] transition-all hover:bg-blue-700"
            >
              {t('dashboard.page.viewPlans')}
            </Link>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
            {t('dashboard.page.errorLoad')}
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          </div>
        )}

        {dashboard && vm && dashboard.overview.total_answered === 0 && <DashboardEmptyState />}

        {dashboard && vm && dashboard.overview.total_answered > 0 && (
          <>
            {/* 1 — Recommended hero (full-width) */}
            {vm.recommended && (
              <RecommendedHero
                drill={vm.recommended}
                domain={recommendedDomain}
                whyKey="dashboard.variantB.recommended.whyDefault"
                examWeight={examWeight}
              />
            )}

            {/* 2 — Certification path + Skill IQ side rail */}
            <section className="mb-7">
              <SectionHead
                title={t('dashboard.variantB.sections.pathTitle', { cert: certName ?? '' })}
                sub={t('dashboard.variantB.sections.pathSub')}
                linkHref="/study"
                linkLabel={t('dashboard.variantB.sections.editPath')}
              />
              <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
                <LearningPath steps={vm.pathSequence} />
                <div className="flex flex-col gap-3.5">
                  {vm.globalIQ && <SkillIQCard iq={vm.globalIQ} />}
                  {vm.nextMilestone && <NextMilestoneCard milestone={vm.nextMilestone} />}
                </div>
              </div>
            </section>

            {/* 3 — Recommended drills carousel */}
            {vm.drills.length > 0 && (
              <section className="mb-7">
                <SectionHead
                  title={t('dashboard.variantB.sections.drillsTitle')}
                  sub={t('dashboard.variantB.sections.drillsSub')}
                  linkHref="/study"
                  linkLabel={t('dashboard.variantB.sections.seeAllDrills')}
                />
                <DrillsCarousel drills={vm.drills} whyTemplateKey="dashboard.variantB.drills.whyTemplate" />
              </section>
            )}

            {/* Ad between drills and catalog */}
            <InlineAd slot="XXXXXXXXXX" className="mb-7" />

            {/* 4 — All domains catalog */}
            <section className="mb-7">
              <SectionHead
                title={t('dashboard.variantB.sections.catalogTitle')}
                sub={t('dashboard.variantB.sections.catalogSub')}
                linkHref="/study"
                linkLabel={t('dashboard.variantB.sections.browseCatalog')}
              />
              <DomainCatalog domains={vm.domains} passMark={passMark} />
            </section>

            {/* 5 — Score trajectory */}
            <section className="mb-7">
              <SectionHead
                title={t('dashboard.variantB.sections.progressTitle')}
                sub={t('dashboard.variantB.sections.progressSub')}
              />
              <ScoreTrajectory trend={vm.scoreTrend} passMark={passMark} />
            </section>

            {/* Footer ad */}
            <InlineAd slot="XXXXXXXXXX" className="mb-4" />

            {/* Reset progress — moved to a less prominent spot since the design
                doesn't expose it. Still mandatory for the app. */}
            <div className="mt-8 flex justify-end">
              <button
                onClick={() => setShowReset(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 dark:border-red-500/20 dark:text-red-400 dark:hover:bg-red-500/10"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                {t('dashboard.page.resetButton')}
              </button>
            </div>
          </>
        )}
    </AppShell>
  );
}
